import * as echarts from "echarts";
import { default as EChartsReact, default as ReactEcharts } from "echarts-for-react";
import { t } from "i18next";
import _ from "lodash";
import { DateTime, Interval } from "luxon";
import { useEffect, useState } from "react";
import ReactDomServer from "react-dom/server";
import { blocksTypes } from "../../../../../enums/blocks";
import { useAppStore } from "../../../../../stores/RootStore";
import { DataRow, EquipmentReportBlock, EquipmentReportTableData, ReportBlockFilters, SplitBy, Tab, useTabsStore, WidgetParameter, XParameter, YParameter } from "../../../../../stores/TabsStore";
import { DateShift, Equipment, Event, Person } from "../../../../../types/types";
// import { ExcludeFilters, getExcludeDataText, getExcludeFilters } from "../ExcludeData/ExcludeData";
// import { AccData, splitTextAndGetHeight } from "../PDF";
import { calculateEquipmentParameter } from "../../../../../lib/calculations";

let counter = 0;

const EHeatMap = ({
  data
}: {
  data: DataRow[];
}) => {
  const [ref, setRef] = useState<EChartsReact | null>();
  const {equipments} = useAppStore()
  const [loading, setLoading] = useState(true)
  const days = _.uniq(data.map(r => r.dateShift.start_day.toSQL()))
  const shifts: DateShift[] = _.orderBy(_.uniqBy(data.map(r => r.dateShift), el => el.id), "start_time")
  const parameter = "availability.worked" as WidgetParameter
  const baseParameter = "availability.total" as WidgetParameter
  const x_parameter = "hours" as XParameter
  const y_parameter = "days" as YParameter
  const [filteredData, setFilteredData] = useState<DataRow[]>()
  const [grpahStart, setGraphStart] = useState<number>(0)
  const [grpahEnd, setGraphEnd] = useState<number>(86400)
  const [xAxis, setXAxis] = useState<string[]>([])
  const [yAxis, setYAxis] = useState<string[]>([])
  const [chartData, setChartData] = useState<(number | string)[][]>([])


  useEffect(() => {
    setLoading(true)
    setFilteredData(data)
  }, [])

  useEffect(() => {
    if(filteredData && filteredData.length > 0) {
      let first_shift_start = shifts[0]?.start_time.toSeconds()! - shifts[0]!.start_day.toSeconds()!;
      let last_shift_end = shifts[shifts.length - 1].end_time.toSeconds() - shifts[0]!.start_day.toSeconds()!;
    
      setGraphStart(first_shift_start)
      setGraphEnd(last_shift_end)

      let c = blockCalculations(filteredData!, parameter!, baseParameter!, x_parameter!, y_parameter!)
      setXAxis(c.xAxis)
      setYAxis(c.yAxis)
      setChartData(c.chartData)
      setLoading(false)
    }
  }, [filteredData])

  const getOptions = () => ({
    tooltip: {
      position: "top",
      formatter: function (params: { marker: any; name: any; value: (number | string | undefined)[] }) {
        return ReactDomServer.renderToStaticMarkup(<TooltipHeatmap params={params} />);
      }
    },
    toolbox: {
      show: false,
      left: 0,
      bottom: 0,
      feature: {
        saveAsImage: {
          show: true,
          excludeComponents: ['toolbox', 'visualMap']
        },
      }
    },
    grid: {
      left: "0%",
      right: "0%",
      bottom: "60px",
      top: "0px",
      containLabel: true,
      height: 30 * days!.length! + 8,
    },
    xAxis: {
      type: 'category',
      position: 'top',
      data: xAxis,
      axisLabel: {
        formatter: function (val: string) {
          if(x_parameter === "hours") {
            return `${val}:00`;
          } else {
            return DateTime.fromISO(val).toFormat("dd.MM");
          }
        },
      },
      splitArea: {
        show: true
      }
    },
    yAxis: {
      type: 'category',
      position: 'right',
      data: yAxis,
      axisLabel: {
        formatter: function (val: string) {
          if(y_parameter === "days") {
            return DateTime.fromISO(val).toFormat("dd.MM");
          } else {
            return val
          }
        },
        width: 100,
        overflow: "break"
      },
      splitArea: {
        show: true
      }
    },
    visualMap: {
      show: false,
      min: 0,
      max: 100,
      calculable: true,
      orient: 'horizontal',
      left: 'right',
      bottom: '0%',
      inRange : {   
        color: ['#FFFFFF', '#57a773', '#157145' ] //From smaller to bigger value ->
      }
    },
    series: [
      {
        name: 'Загрузка оборудования за',
        type: 'heatmap',
        data: chartData,
        label: {
          show: true,
          formatter: function (params: { marker: any; name: any; value: (number | string | undefined)[] }) {
            return params.value[2];
          },
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  });

  return (
    <ReactEcharts
      option={getOptions()}
      ref={(e) => { setRef(e);}}
      style={{ width: "100%", height: 30 * days.length! + 80 }}
      opts={{ renderer: "canvas" }}
    />
  );
}

function blockCalculations(filteredData: DataRow[], parameter: WidgetParameter, baseParameter: WidgetParameter, x_parameter: XParameter, y_parameter: YParameter):{xAxis: string[], yAxis: string[], chartData: (number | string)[][]} {
  let yGrouppedData: {[x:string]: DataRow[]} = {}
  if(y_parameter === "days") {
    yGrouppedData = _.groupBy(filteredData, el => el.dateShift.start_day.toSQLDate())
  } else if(y_parameter === "equipments") {
    yGrouppedData = _.groupBy(filteredData, el => el.equipment.attributes.name)
  } else {
    yGrouppedData = {}
  }

  let yAxis: string[] = []
  let xAxis: string[][] = []
  let chartData: (number | string)[][] = []

  Object.entries(yGrouppedData).map(([yEl, yRows]:[string, DataRow[]], yIndex) => {
    yAxis.push(yEl)
    let xGrouppedData: {[x:string]: Event[]} = {}
    if(x_parameter === "days" && y_parameter !== "days") {
      xAxis.push(_.uniqBy(yRows.map(r => r.events).flat(), el => DateTime.fromSQL(String(el[3])).toSQLDate()).map(x => DateTime.fromSQL(String(x[3])).toSQLDate()!))
      xGrouppedData = _.groupBy(yRows.map(r => r.events).flat(), el => DateTime.fromSQL(String(el[3])).toSQLDate())
    } else if(x_parameter === "hours") {
      xAxis.push(_.uniqBy(yRows.map(r => r.events).flat(), el => el[17]).map(x => String(x[17])))
      _.uniqBy(yRows.map(r => r.events).flat(), el => el[17]).map(x => Number(x[17])).map(h => xGrouppedData[h] = yRows.map(r => r.events).flat().filter(e => e[17] === h))
    } else {
      xGrouppedData = {}
    }
    Object.entries(xGrouppedData).map(([xEl, xEvents]:[string, Event[]]) => {
      let xIndex = xAxis.flat().findIndex(x => x === xEl)
      chartData.push([xIndex, yIndex, Math.round((calculateEquipmentParameter(xEvents, parameter) / calculateEquipmentParameter(xEvents, baseParameter)) * 100), yEl, Math.round((calculateEquipmentParameter(xEvents, parameter) / calculateEquipmentParameter(xEvents, baseParameter)) * 100)])
    })
  })

  return {yAxis: yAxis, xAxis: _.uniq(xAxis.flat()), chartData: chartData}
}

const TooltipHeatmap = ({ params }: { params: { name: any; value: (string | number | undefined)[] } }) => {
  return (
    <div className="flex flex-col w-[16rem]">
      <div>Загрузка оборудования за:</div>
      <div className="capitalize">{params.value[3]}</div>
    </div>
  );
};

export default EHeatMap;


// export function getHeatMapByHoursPdf(state: AccData, tab: Tab, data: EquipmentReportTableData[], equipments: Equipment[], persons: Person[]) {
//   const filters = state.block?.filters
//   let filteredData = data.filter(row => 
//     filters?.days.includes(row.at.toISODate()!) &&
//     filters?.weekdays.includes(String(row.at.weekday)) &&
//     filters?.shifts.includes(row.shift.id) &&
//     filters?.equipments.includes(row.equipment_item_id) &&
//     (state.block?.options?.active_rows ? row.worked > 600 : true)
//   )
//   let excludeFilters: ExcludeFilters = getExcludeFilters(data, filteredData)

//   let blockDoc = state.doc
//   let stateConfig = state.config

//   let domBlock =  document.getElementById(`block-${state.block?.id}`)?.getElementsByClassName("echarts-for-react").item(0)! as HTMLElement
//   let echartInstance = echarts.getInstanceByDom(domBlock)
//   let graphHeight = echartInstance?.getHeight()! * 0.16
//   let base64 = echartInstance?.getDataURL();

//   let label = splitTextAndGetHeight(blockDoc, state.block?.name ? state.block?.name : "", 150)
//   let subLabelText = splitTextAndGetHeight(blockDoc, `${blocksTypes.find(x => x.value === state.block?.type)?.label} ${state.block?.options?.active_rows ? t('active_rows') : ""}`, 150)
//   let excludesText = splitTextAndGetHeight(blockDoc, getExcludeDataText(excludeFilters, equipments), 150)

//   if(stateConfig.currentHeight + subLabelText.height + 0.5 + excludesText.height + 0.5 + graphHeight * 0.75 > state.config.docHeight - 20) {
//     blockDoc.addPage()  
//     stateConfig.currentHeight = 20
//   }

//   // Label
//   blockDoc.setFont("Roboto", "medium")
//   blockDoc.setFontSize(state.config.labelTextSize)
//   blockDoc.text(label.text, 10, stateConfig.currentHeight, {align: "left"})
//   stateConfig.currentHeight += label.height + 2

//   // SubLabel
//   blockDoc.setFont("Roboto", "light")
//   blockDoc.setFontSize(state.config.hintTextSize)
//   blockDoc.text(subLabelText.text, 10, stateConfig.currentHeight, {align: "left"})
//   stateConfig.currentHeight += subLabelText.height + 0.5

//   // Excludes
//   blockDoc.setFont("Roboto", "light")
//   blockDoc.setFontSize(state.config.hintTextSize)
//   blockDoc.text(excludesText.text, 10, stateConfig.currentHeight, {align: "left"})
//   stateConfig.currentHeight += excludesText.height + 0.5

//   blockDoc.addImage(base64!, "jpeg", 10, stateConfig.currentHeight, 190, graphHeight)

//   stateConfig.currentHeight += graphHeight + 6

//   return {...state, doc: blockDoc, config: stateConfig}
// }